import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslations } from '@/lib/i18n/useTranslations';
import { HELP_INDEX_PATH } from '@/modules/help/constants/help-routes';
import { isInstalledPWA, isIOS, isAndroid } from '@/lib/pwa/is-pwa';
import { isMobileUserAgent } from '@/lib/pwa/is-mobile';

export function PWAEnforcer({ children }: { children: React.ReactNode }) {
  const t = useTranslations('pwa');
  const { pathname } = useLocation();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <>{children}</>;
  }

  if (isInstalledPWA()) {
    return <>{children}</>;
  }

  const isPublicDocRoute = pathname?.startsWith('/p/') ?? false;
  const isHelpRoute =
    pathname === HELP_INDEX_PATH || (pathname?.startsWith(`${HELP_INDEX_PATH}/`) ?? false);

  if (isPublicDocRoute || isHelpRoute) {
    return <>{children}</>;
  }

  if (!isMobileUserAgent(navigator.userAgent)) {
    return <>{children}</>;
  }

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white p-6 text-center">
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-[#c1121f] text-white">
        <span className="text-4xl font-bold">V</span>
      </div>
      <h1 className="mb-4 text-2xl font-bold text-gray-900">{t('enforcement.title')}</h1>
      <p className="mb-8 max-w-sm text-gray-600">{t('enforcement.description')}</p>

      {isAndroid() && (
        <div className="max-w-sm rounded-lg border border-gray-200 bg-gray-50 p-4 text-left">
          <p className="mb-2 text-sm font-medium text-gray-900">{t('enforcement.android.title')}</p>
          <p className="text-sm text-gray-600">{t('enforcement.android.instructions')}</p>
        </div>
      )}

      {isIOS() && (
        <div className="max-w-sm rounded-lg border border-gray-200 bg-gray-50 p-4 text-left">
          <p className="mb-3 text-sm font-medium text-gray-900">{t('enforcement.ios.title')}</p>
          <ol className="list-decimal space-y-2 pl-5 text-sm text-gray-600">
            <li>{t('enforcement.ios.step1')}</li>
            <li>{t('enforcement.ios.step2')}</li>
            <li>{t('enforcement.ios.step3')}</li>
          </ol>
        </div>
      )}
    </div>
  );
}
